// @ts-nocheck
import type { ContentFragment } from '@oneaudi/falcon-tools';
import type { AuthorDefinedFilters } from '../models/map/author-config';
import type { DistanceUnit } from '../models/common';
import type { MyAudiBarButtonConfig } from '../types';
import type { ctaBarButtonConfigProps } from '../components/map/PartnerBusinessCard/pbc-types';

export interface EditorJsonContent {
  __type?: 'editor.json';
  devMode: boolean;
  marketGoogleKey: string;
  intlSearchEnabled: boolean;
  filters: AuthorDefinedFilters;
  preselectedFilters: string[];
  initialSearchRadius: string;
  initialMapZoomLevel: string;
  showAddress: boolean;
  groupMarkers: boolean;
  distanceUnit: DistanceUnit;
  showPartnerSuggestions: boolean;
  ctaBarButtonConfig: ctaBarButtonConfigProps[];
  displayCtaBar: boolean;
  showOpeningHours: boolean;
  pbcVersion?: string;
  twoClickConsentRequired: boolean;
  twoClick: {
    headline: string;
    copyText: string;
    toggleLabel: string;
    asideToggleHeadline: string;
    asideToggleLabel: string;
    asideToggleCopy: string;
    closeButton: {
      text: string;
      redirectURL: string;
    };
  };
  fiveStar: {
    displayFiveStarRating: boolean;
    legalText: string;
    showComments: boolean;
  };
  myAudiCtaConfig?: MyAudiBarButtonConfig | null;
  connectDealer: boolean;
}

export interface FalconContent extends Partial<ContentFragment> {
  __type: 'aem-headless';
  fields: {
    displayCtaBar: boolean;
    displayFiveStarRating: boolean;
    distanceUnit: DistanceUnit;
    'accident-specialist': boolean;
    atyourdoor: boolean;
    'audi-on-demand': boolean;
    'silver-car-by-audi': string;
    etron: boolean;
    'sales-r8': boolean;
    'sales-rs': boolean;
    sales: boolean;
    service: boolean;
    usedCars: boolean;
    groupMarkers: boolean;
    initialMapZoomLevel: string;
    initialSearchRadius: string;
    intlSearchEnabled: boolean;
    marketGoogleKey: string;
    preselectedFilters: string[];
    showAddress: boolean;
    showPartnerSuggestions: boolean;
    ctaBarButtonConfig: ctaBarButtonConfigProps[];
    showOpeningHours: true;
    devMode: true;
    pbcVersion?: string;
    twoClickConsentRequired: true;
    twoClickHeadline: string;
    twoClickCopyText: string;
    twoClickToggleLabel: string;
    twoClickAsideToggleHeadline: string;
    twoClickAsideToggleLabel: string;
    twoClickAsideToggleCopy: string;
    twoClickCloseButtonText: string;
    twoClickCloseButtonRedirectURL: string;
    fiveStarDisplayFiveStarRating: boolean;
    fiveStarLegalText: string;
    connectDealer: boolean;
    autoConnectDealer: boolean;
    showComments: boolean;
  };
}

export interface ctaBarButtonConfigHeadlessProps {
  fields: ctaBarButtonConfigProps;
}

export function mapContent(
  content: EditorJsonContent | FalconContent | undefined,
): EditorJsonContent | undefined {
  if (!content || content.__type !== 'aem-headless') {
    return content;
  }

  const { fields } = content;
  const ctaBarButtonConfig = (ctaBarData: ctaBarButtonConfigHeadlessProps[]) =>
    ctaBarData.map((data) => data.fields);

  const {
    devMode,
    marketGoogleKey,
    intlSearchEnabled,
    preselectedFilters,
    initialSearchRadius,
    initialMapZoomLevel,
    showAddress,
    distanceUnit,
    groupMarkers,
    showOpeningHours,
    showPartnerSuggestions,
    displayCtaBar,
    twoClickConsentRequired,
    twoClickAsideToggleCopy,
    atyourdoor,
    etron,
    sales,
    service,
    usedCars,
    twoClickAsideToggleHeadline,
    twoClickAsideToggleLabel,
    twoClickCloseButtonRedirectURL,
    twoClickCloseButtonText,
    twoClickCopyText,
    twoClickHeadline,
    twoClickToggleLabel,
    fiveStarLegalText,
    fiveStarDisplayFiveStarRating,
    connectDealer,
    autoConnectDealer,
    showComments,
  } = fields;

  const mappedContent = {
    devMode,
    marketGoogleKey,
    intlSearchEnabled,
    connectDealer,
    autoConnectDealer,
    filters: {
      sales,
      service,
      usedcars: usedCars,
      etron,
      'sales-rs': fields['sales-rs'],
      'sales-r8': fields['sales-r8'],
      'audi-on-demand': fields['audi-on-demand'],
      'accident-specialist': fields['accident-specialist'],
      atyourdoor,
      'silver-car-by-audi': fields['silver-car-by-audi'],
    } as AuthorDefinedFilters,
    preselectedFilters,
    initialSearchRadius,
    initialMapZoomLevel,
    showAddress,
    groupMarkers,
    distanceUnit,
    showPartnerSuggestions,
    ctaBarButtonConfig: ctaBarButtonConfig(fields.ctaBarButtonConfig),
    displayCtaBar,
    showOpeningHours,
    twoClickConsentRequired,
    twoClick: {
      headline: twoClickHeadline,
      copyText: twoClickCopyText,
      toggleLabel: twoClickToggleLabel,
      asideToggleHeadline: twoClickAsideToggleHeadline,
      asideToggleLabel: twoClickAsideToggleLabel,
      asideToggleCopy: twoClickAsideToggleCopy,
      closeButton: {
        text: twoClickCloseButtonText,
        redirectURL: twoClickCloseButtonRedirectURL,
      },
    },
    fiveStar: {
      displayFiveStarRating: fiveStarDisplayFiveStarRating,
      legalText: fiveStarLegalText,
      showComments,
    },
  };

  const normalizedContent: EditorJsonContent = {
    ...mappedContent,
  };

  return normalizedContent;
}
